import { FC } from 'react';
import { Text, View } from '@react-pdf/renderer';

import Table from '../PdfTable';
import styles from './styles';

interface LossesProps {
  taxYear: string;
  losses: Array<{
    title: string;
    date: string;
    capitalAllocated: string;
    sharesSold: number;
    saleValue: string;
    isGain: boolean;
    formattedValue: string;
    isTaxReliefElegible: boolean;
  }>;
  totals: {
    elegible: string;
    other: string;
  };
}

const Losses: FC<LossesProps> = ({ taxYear, losses, totals }) => {
  if (!losses.length) {
    return (
      <View wrap={false}>
        <Text style={styles.heading}>Realised losses</Text>
        <Text style={styles.paragraph}>
          You did not receive any losses from realisations during the {taxYear}{' '}
          tax year.
        </Text>
      </View>
    );
  }

  return (
    <>
      <View wrap={false}>
        <Text style={styles.heading}>Realised losses</Text>
        <Text style={styles.paragraph}>
          You made the following losses from realisations this tax year:
        </Text>
      </View>

      <Table
        title={null}
        backgroundColor="white"
        fontColor="#333333"
        alignContent="left"
        fontSize={8}
        fullBorder
        expandRows
        columns={{
          investment: { value: 'Investment', bold: true },
          investmentDate: {
            value: 'Realisation date',
            bold: true,
            width: 12,
          },
          shares: {
            value: 'Shares/units realised',
            bold: true,
            width: 12,
          },
          capitaAllocated: {
            value: 'Initial purchase value',
            bold: true,
            width: 15,
          },
          saleValue: {
            value: 'Sale proceeds (before fees)',
            bold: true,
            width: 15,
          },
          elegibleValue: {
            value: 'S/EIS eligible loss',
            bold: true,
            width: 15,
          },
          otherValue: {
            value: 'Other loss',
            bold: true,
            width: 15,
          },
        }}
        data={[
          ...losses.map((loss) => ({
            investment: {
              value: loss.title,
            },
            investmentDate: {
              value: loss.date,
              width: 12,
            },
            shares: {
              value: loss.sharesSold,
              width: 12,
            },
            capitaAllocated: {
              value: loss.capitalAllocated,
              width: 15,
            },
            saleValue: {
              value: loss.saleValue,
              width: 15,
            },
            elegibleValue: {
              value: loss.isTaxReliefElegible ? loss.formattedValue : '',
              width: 15,
            },
            otherValue: {
              value: !loss.isTaxReliefElegible ? loss.formattedValue : '',
              width: 15,
            },
          })),
        ]}
        footer={{
          investment: {
            value: '',
          },
          investmentDate: {
            value: '',
            width: 12,
          },
          shares: {
            value: '',
            width: 12,
          },
          capitaAllocated: {
            value: '',
            width: 15,
          },
          saleValue: {
            value: '',
            width: 15,
          },
          elegibleValue: {
            value: totals.elegible,
            bold: true,
            width: 15,
          },
          otherValue: {
            value: totals.other,
            bold: true,
            width: 15,
          },
        }}
      />
      <Text style={[styles.paragraph, styles.topMargin]}>
        Any S/EIS eligible loss above is calculated as (i) the investment’s
        original cost minus (ii) eligible income tax relief minus (ii) the
        actual loss.
      </Text>
      <Text style={styles.paragraph}>
        S/EIS eligible losses are eligible for HMRC loss relief treatment and
        may be included in your HMRC tax return. Your level of claimable loss
        relief is based on the UK income or capital gains tax rate you claim the
        loss against. This may be up to 45% of the above S/EIS eligible loss
        amount if you are a UK additional rate taxpayer claiming against your
        income tax liability.
      </Text>
      <Text style={styles.paragraph}>
        Other losses may be eligible for 'qualifying trading company' loss
        relief. Please seek professional tax advice or contact HMRC for more
        information about this. The availability of this relief may also be
        outlined in the relevant investment's exit statement.
      </Text>
      <Text style={styles.paragraph}>
        Where an escrow payment or historically taxable earnout is lost, the{' '}
        <Text style={styles.italic}>Initial purchase value</Text> field will
        display the aggregate exit value received to date, to allow you to
        easily review the new additional loss you have incurred.
      </Text>
      <Text style={styles.paragraph}>
        If you pay tax in a non-UK jurisdiction, please seek professional tax
        advice before deciding which of the above realised losses should be
        declared in your applicable jurisdiction.
      </Text>
      <View wrap={false}>
        <Text style={styles.paragraph}>
          <Text style={styles.bold}>Note on previous year realised losses</Text>
        </Text>
        <Text style={styles.paragraph}>
          If you see a realised loss above with a realisation date from a{' '}
          <Text style={styles.bold}>past tax year</Text>, this can either be
          ignored (if an S/EIS eligible loss) or acted upon (if in the ‘Other
          loss’ column).
        </Text>
      </View>
      <Text style={styles.paragraph}>
        This type of loss means a past realisation has reduced in value. Losses
        like this usually arise when unpaid sale proceeds are lost to legal
        warranty claims. If you have such a loss above and have any questions,
        please contact your applicable fund manager.
      </Text>
      <Text style={styles.paragraph}>
        UK taxpayers may be eligible to reclaim tax paid on such a value
        reduction. If you previously paid tax on the relevant realisation, to
        reclaim tax paid you must amend your previous year’s tax return to
        reflect the value reduction above. If in doubt, please seek professional
        advice.
      </Text>
    </>
  );
};

export default Losses;
